import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import { DocumentRequirementsProvider } from "./context/DocumentRequirementsContext";
import { GoogleMapsProvider } from "./context/GoogleMapsContext";
import { API_URL } from "./config/apiConfig";
import "react-toastify/dist/ReactToastify.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./assets/layout-utils.css";

// Layouts
import Header from "./layouts/Header";
import Footer from "./layouts/Footer";
import AdminLayout from "./layouts/AdminLayout";

// Public Pages
import Home from "./pages/Home";
import OneWay from "./pages/OneWay";
import RoundTrip from "./pages/RoundTrip";
import Login from "./pages/Login";
import VerifyOtp from "./pages/VerifyOtp";
import Register from "./pages/Register";
import CorporateLogin from "./pages/CorporateLogin";
import CorporateRegister from "./pages/CorporateRegister";
import CorporateAdminDashboard from "./pages/CorporateAdminDashboard";
import CorporateDashboard from "./pages/CorporateDashboard";
import CorporateProfile from "./pages/CorporateProfile";
import CorporateStaff from "./pages/CorporateStaff";
import CorporateApprovals from "./pages/CorporateApprovals";
import CorporatePendingApprovals from "./pages/CorporatePendingApprovals";
import CorporateApproved from "./pages/CorporateApproved";
import CorporateRejected from "./pages/CorporateRejected";
import CorporateWallet from "./pages/CorporateWallet";
import CorporateBookings from "./pages/CorporateBookings";
import CorporateLayout from "./layouts/CorporateLayout";
import BookingTrack from "./pages/BookingTrack";
import NotFound from "./pages/NotFound";
import CarSelection from "./pages/CarSelection";
import CustomerDetails from "./pages/CustomerDetails";
import PaymentPage from "./pages/PaymentPage";
import ClearStorage from "./pages/ClearStorage";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Refund from "./pages/Refund";
import CancellationPolicyPublic from "./pages/CancellationPolicy";
import FAQsPublic from "./pages/FAQs";
import HowToUsePublic from "./pages/HowToUse";
import SupportDetailsPublic from "./pages/SupportDetails";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import MediaDetail from "./pages/MediaDetail";
import About from "./pages/About";
import Drivers from "./pages/Drivers";
import BlogManagement from "./pages/Admin/BlogManagement";
import AdsManagement from "./pages/Admin/AdsManagement";
import MaintenanceManagement from "./pages/Admin/MaintenanceManagement";
import AuthImageManagement from "./pages/Admin/AuthImageManagement";
import LiveTracking from "./pages/LiveTracking";
import TrackingSimulator from "./pages/TrackingSimulator";

import RouteRedirect from "./components/RouteRedirect";
import FloatingSupport from "./components/FloatingSupport";
import ScrollToTopButton from "./components/ScrollToTopButton";
import { getCorporateToken, getCorporateMember } from "./utils/corporateSession";

// Protected Pages
import Dashboard from "./pages/Dashboard";
import BookingHistory from "./pages/BookingHistory";
import Bookings from "./pages/Bookings";
import BookingDetails from "./pages/BookingDetails";

import Profile from "./pages/Profile";
import Payment from "./pages/Payment";
import PaymentSuccess from "./pages/PaymentSuccess";
import UserWallet from "./pages/UserWallet";

// Admin Pages
import AdminLogin from "./pages/Admin/AdminLogin";
import AdminDashboard from "./pages/Admin/Dashboard";
import AdminHomeDirectory from "./pages/Admin/HomeDirectory";
import AdminUsers from "./pages/Admin/Users";
import AdminRoles from "./pages/Admin/RoleManagement";
import AdminRolePermissions from "./pages/Admin/RolePermissions";
import AdminCreateStaff from "./pages/Admin/CreateStaff";
import AdminBookings from "./pages/Admin/Bookings";
import AbandonedBookings from "./pages/Admin/AbandonedBookings";
import AdminCorporates from "./pages/Admin/Corporates";
import AdminCabs from "./pages/Admin/Cabs";
import AdminVehicleLocations from "./pages/Admin/VehicleLocations";
import AdminLiveTracking from "./pages/Admin/AdminLiveTracking";
import AdminDriverManagement from "./pages/Admin/DriverManagement";
import AdminBookingAssignment from "./pages/Admin/BookingAssignment";
import DocumentConfiguration from "./pages/Admin/DocumentConfiguration";
import BookingSettings from "./pages/Admin/BookingSettings";
import CancellationPolicy from "./pages/Admin/CancellationPolicy";
import FAQManagement from "./pages/Admin/FAQManagement";
import HowToUseAppManagement from "./pages/Admin/HowToUseAppManagement";
import TestimonialManagement from "./pages/Admin/TestimonialManagement";
import MediaManagement from "./pages/Admin/MediaManagement";
import SupportDetailsPage from "./pages/Admin/SupportDetails";
import SupportMessagesPage from "./pages/Admin/SupportMessages";
import PopularRouteManagement from "./pages/Admin/PopularRouteManagement";
import PopularCities from "./pages/Admin/PopularCities";
import FooterRoutes from "./pages/Admin/FooterRoutes";
import Airports from "./pages/Admin/Airports";
import RentalPackages from "./pages/Admin/RentalPackages";

import WalletSummary from "./pages/Admin/WalletSummary";
import WalletDrivers from "./pages/Admin/WalletDrivers";
import WalletTransactions from "./pages/Admin/WalletTransactions";
import PendingWithdrawals from "./pages/Admin/PendingWithdrawals";
import UserWithdrawals from "./pages/Admin/UserWithdrawals";
import CouponManagement from "./pages/Admin/CouponManagement";
import VendorManagement from "./pages/Admin/VendorManagement";
import CommissionManagement from "./pages/Admin/CommissionManagement";
import AdminCorporateWallet from "./pages/Admin/CorporateWallet";
import AppVersion from "./pages/Admin/AppVersion";
import RideIssues from "./pages/Admin/RideIssues";


// Driver Pages
import DriverLogin from "./pages/driver/DriverLogin";
import DriverRegister from "./pages/driver/DriverRegister";
import ForgotPassword from "./pages/ForgotPassword";
import SendOtp from "./pages/forgot-password/SendOtp";
import ResetPassword from "./pages/forgot-password/ResetPassword";
import DriverDashboard from "./pages/driver/DriverDashboard";
import DriverProfile from "./pages/driver/DriverProfile";
import CurrentRide from "./pages/driver/CurrentRide";
import RideHistory from "./pages/driver/RideHistory";
import NewRideRequests from "./pages/driver/NewRideRequests";
import WalletWithdraw from "./pages/driver/WalletWithdraw";
import WalletHistory from "./pages/driver/WalletHistory";
import WalletCommission from "./pages/driver/WalletCommission";

// Vendor Pages
import VendorLogin from "./pages/vendor/VendorLogin";
import VendorRegister from "./pages/vendor/VendorRegister";
import VendorDashboard from "./pages/vendor/VendorDashboard";
import VendorSetPassword from "./pages/vendor/VendorSetPassword";
import VendorDriverAdd from "./pages/vendor/VendorDriverAdd";
import VendorWalletHistory from "./pages/vendor/VendorWalletHistory";
import VendorWithdraw from "./pages/vendor/VendorWithdraw";

// Components
import PrivateRoute from "./components/PrivateRoute";
import AdminRoute from "./components/AdminRoute";
import DriverRoute from "./components/DriverRoute";
import VendorRoute from "./components/VendorRoute";
import ScrollToTop from "./components/ScrollToTop";
import DriverHeader from "./layouts/DriverHeader";
import DriverAuthMonitor from "./components/DriverAuthMonitor";

// Google Maps API configuration
const googleMapsConfig = {
  googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  libraries: ["places", "geometry"],
  id: "google-maps-script",
  preventGoogleFontsLoading: true,
  version: "beta",
  language: "en",
  region: "IN",
};

function App() {
  // Initialize configuration verification and socket connection
  useEffect(() => {
    // Verify configuration is correct (helps debug production issues)
    const { initializeConfigCheck } = require("./utils/configVerification");
    initializeConfigCheck();

    // Import socket service directly to avoid issues with hooks
    const socketService = require("./utils/socketService").default;

    // Connect to socket server at the application level to maintain a single connection
    socketService.connect();

    // Restore and validate corporate session on app load if it exists
    // This ensures that corporate staff session persists across page refreshes
    const validateCorporateSession = async () => {
      const corporateToken = localStorage.getItem("corporateToken");
      const corporateMember = localStorage.getItem("corporateMember");

      if (!corporateToken || !corporateMember) {
        return;
      }

      try {
        console.log("🔍 Validating corporate session with backend...");

        const response = await axios.get(`${API_URL}/corporate/me`, {
          headers: {
            Authorization: `Bearer ${corporateToken}`,
          },
          timeout: 5000,
        });

        if (response?.data?.success) {
          console.log("✅ Corporate session validated successfully");
          // Update localStorage with fresh member data to prevent stale data
          localStorage.setItem("corporateMember", JSON.stringify(response.data.data));
          // Dispatch event to notify all components of corporate session restoration
          window.dispatchEvent(new Event("corporateSessionRestored"));
        } else {
          console.warn("⚠️ Corporate session validation failed: invalid response");
          localStorage.removeItem("corporateToken");
          localStorage.removeItem("corporateMember");
        }
      } catch (err) {
        console.warn("⚠️ Corporate session validation failed, clearing session");
        console.warn("   Error:", err?.response?.status || err?.message);
        // Clear invalid session
        localStorage.removeItem("corporateToken");
        localStorage.removeItem("corporateMember");
      }
    };

    // Run validation
    validateCorporateSession();

    return () => {
      // Only disconnect on full app unmount (rarely happens in production)
      socketService.disconnect();
    };
  }, []);

  return (
    <GoogleMapsProvider options={googleMapsConfig}>
      <DocumentRequirementsProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <ScrollToTop />
          <DriverAuthMonitor />
          <AppContent />
          <FloatingSupport />
          <ScrollToTopButton />
          <ToastContainer position="top-right" autoClose={5000} limit={3} />
        </Router>
      </DocumentRequirementsProvider>
    </GoogleMapsProvider>
  );
}

// Component to redirect dashboard to corporate bookings for corporate staff
function DashboardRedirect() {
  return <Dashboard />;
}

// Component to conditionally render the header based on the current route
function AppContent() {
  // Use the actual useLocation hook from react-router-dom
  const location = useLocation();
  const pathname = location.pathname;
  const { driver } = useSelector((state) => state.driverAuth);

  // Check if we're on any driver page or if the user is logged in as a driver
  const isDriverPage =
    pathname.includes("/driver/dashboard") ||
    pathname.includes("/driver/new-ride-requests") ||
    pathname.includes("/driver/current-ride") ||
    pathname.includes("/driver/ride-history") ||
    pathname.includes("/driver/profile") ||
    pathname.includes("/driver/wallet");

  // Check if we're on any corporate page or admin/vendor pages
  const isAdminPage = pathname.includes("/admin");
  const isVendorPage = pathname.includes("/vendor");
  const isCorporatePage = pathname.includes("/corporate");
  const isLiveTrackingPage = pathname.startsWith("/track/") || pathname === "/track-demo";

  // Only show Header on public pages (home, blogs, etc.)
  const shouldShowHeader = !isAdminPage && !isVendorPage && !isCorporatePage && !isDriverPage && !isLiveTrackingPage;

  return (
    <div className={`flex flex-col min-h-screen ${shouldShowHeader ? 'overflow-x-hidden' : 'overflow-x-auto'}`}>
      {/* Show Driver Header on driver pages, main Header on public pages */}
      {isDriverPage ? (
        <DriverHeader />
      ) : shouldShowHeader ? (
        <Header />
      ) : null}

      <main className={`flex-grow ${(!pathname.includes("/admin") && !pathname.includes("/vendor") && !pathname.includes("/corporate") && !isDriverPage && !pathname.startsWith("/track/")) ? ((pathname.toLowerCase().startsWith("/cabs") || pathname.toLowerCase().startsWith("/booking/")) ? "pt-0 md:pt-20 lg:pt-20 2xl:pt-20 w-full overflow-x-hidden" : "page-container") : ""}`}>
        <Routes>
          {/* Live Tracking – standalone, no header/footer */}
          <Route path="/track/:rideId" element={<LiveTracking />} />
          <Route path="/track-demo" element={<TrackingSimulator />} />

          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/route/:route" element={<RouteRedirect />} />
          <Route path="/city/one-way-cab" element={<OneWay />} />
          <Route path="/city/round-trip-cab" element={<RoundTrip />} />
          <Route path="/login" element={<Login />} />
          <Route path="/verify-otp" element={<VerifyOtp />} />
          <Route path="/corporate/login" element={<CorporateLogin />} />
          <Route path="/corporate/register" element={<CorporateRegister />} />
          <Route
            path="/corporate/dashboard"
            element={
              <CorporateLayout>
                <CorporateDashboard />
              </CorporateLayout>
            }
          />

          <Route
            path="/corporate/admin-dashboard"
            element={
              <CorporateLayout>
                <CorporateAdminDashboard />
              </CorporateLayout>
            }
          />
          <Route
            path="/corporate/wallet"
            element={
              <CorporateLayout>
                <CorporateWallet />
              </CorporateLayout>
            }
          />
          <Route
            path="/corporate/profile"
            element={
              <CorporateLayout>
                <CorporateProfile />
              </CorporateLayout>
            }
          />
          <Route
            path="/corporate/staff"
            element={
              <CorporateLayout>
                <CorporateStaff />
              </CorporateLayout>
            }
          />
          <Route
            path="/corporate/approvals"
            element={
              <CorporateLayout>
                <CorporateApprovals />
              </CorporateLayout>
            }
          />
          <Route
            path="/corporate/pending-approvals"
            element={
              <CorporateLayout>
                <CorporatePendingApprovals />
              </CorporateLayout>
            }
          />
          <Route
            path="/corporate/approved"
            element={
              <CorporateLayout>
                <CorporateApproved />
              </CorporateLayout>
            }
          />
          <Route
            path="/corporate/rejected"
            element={
              <CorporateLayout>
                <CorporateRejected />
              </CorporateLayout>
            }
          />
          <Route
            path="/corporate/bookings"
            element={
              <CorporateLayout>
                <CorporateBookings />
              </CorporateLayout>
            }
          />
          <Route
            path="/corporate/book-ride"
            element={<Navigate to="/" replace />}
          />
          <Route path="/register" element={<Register />} />
          <Route path="/:role/change-password" element={<ForgotPassword />} />
          <Route path="/user/forgot-password/send-otp" element={<SendOtp />} />
          <Route path="/user/reset-password" element={<ResetPassword />} />
          <Route path="/driver/forgot-password/send-otp" element={<SendOtp role="driver" />} />
          <Route path="/driver/reset-password" element={<ResetPassword role="driver" />} />
          <Route path="/vendor/forgot-password/send-otp" element={<SendOtp role="vendor" />} />
          <Route path="/vendor/reset-password" element={<ResetPassword role="vendor" />} />
          <Route path="/admin/forgot-password/send-otp" element={<SendOtp role="admin" />} />
          <Route path="/admin/reset-password" element={<ResetPassword role="admin" />} />
          <Route path="/track-booking" element={<BookingTrack />} />
          <Route path="/cabs" element={<CarSelection />} />
          <Route
            path="/booking/customer-details"
            element={<CustomerDetails />}
          />
          <Route path="/booking/payment" element={<PaymentPage />} />
          <Route path="/driver/login" element={<DriverLogin />} />
          <Route path="/driver/register" element={<DriverRegister />} />
                    <Route path="/vendor/login" element={<VendorLogin />} />
          <Route path="/vendor/register" element={<VendorRegister />} />
          <Route path="/vendor/set-password" element={<VendorSetPassword />} />
          <Route
            path="/vendor/dashboard"
            element={
              <VendorRoute>
                <VendorDashboard />
              </VendorRoute>
            }
          />
          <Route
            path="/vendor/drivers/add"
            element={
              <VendorRoute>
                <VendorDriverAdd />
              </VendorRoute>
            }
          />
          <Route
            path="/vendor/wallet/history"
            element={
              <VendorRoute>
                <VendorWalletHistory />
              </VendorRoute>
            }
          />
          <Route
            path="/vendor/wallet/withdraw"
            element={
              <VendorRoute>
                <VendorWithdraw />
              </VendorRoute>
            }
          />
          <Route path="/clear-storage" element={<ClearStorage />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund" element={<Refund />} />
          <Route path="/cancellation-policy" element={<CancellationPolicyPublic />} />
          <Route path="/faqs" element={<FAQsPublic />} />
          <Route path="/how-to-use" element={<HowToUsePublic />} />
          <Route path="/drivers" element={<Drivers />} />
          <Route path="/support-details" element={<SupportDetailsPublic />} />
          <Route path="/blogs" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/media/:slug" element={<MediaDetail />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <DashboardRedirect />
              </PrivateRoute>
            }
          />
          <Route
            path="/bookings"
            element={
              <PrivateRoute>
                <Bookings />
              </PrivateRoute>
            }
          />
          <Route
            path="/booking/:id"
            element={
              <PrivateRoute>
                <BookingDetails />
              </PrivateRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path="/payment/:bookingId"
            element={
              <PrivateRoute>
                <Payment />
              </PrivateRoute>
            }
          />
          <Route
            path="/payment-success/:bookingId"
            element={
              <PrivateRoute>
                <PaymentSuccess />
              </PrivateRoute>
            }
          />
          <Route
            path="/wallet"
            element={
              <PrivateRoute>
                <UserWallet />
              </PrivateRoute>
            }
          />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin/home"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminHomeDirectory />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminUsers />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminRoles />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/role-permissions"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminRolePermissions />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/create-staff"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminCreateStaff />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/bookings"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminBookings />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/abandoned-bookings"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AbandonedBookings />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/live-tracking"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminLiveTracking />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/corporates"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminCorporates />
                </AdminLayout>
              </AdminRoute>
            }
          />

          <Route
            path="/admin/corporate-wallet"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminCorporateWallet />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/cabs"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminCabs />
                </AdminLayout>
              </AdminRoute>
            }
          />

          <Route
            path="/admin/vehicle-locations"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminVehicleLocations />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/drivers"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminDriverManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/vendors"
            element={
              <AdminRoute>
                <AdminLayout>
                  <VendorManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/commission"
            element={
              <AdminRoute>
                <AdminLayout>
                  <CommissionManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/booking-assignment"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminBookingAssignment />
                </AdminLayout>
              </AdminRoute>
            }
          />

          <Route
            path="/admin/document-settings"
            element={
              <AdminRoute>
                <AdminLayout>
                  <DocumentConfiguration />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/booking-settings"
            element={
              <AdminRoute>
                <AdminLayout>
                  <BookingSettings />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/app-version"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AppVersion />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/cancellation-policy"
            element={
              <AdminRoute>
                <AdminLayout>
                  <CancellationPolicy />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/faqs"
            element={
              <AdminRoute>
                <AdminLayout>
                  <FAQManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/how-to-use"
            element={
              <AdminRoute>
                <AdminLayout>
                  <HowToUseAppManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/testimonials"
            element={
              <AdminRoute>
                <AdminLayout>
                  <TestimonialManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/media"
            element={
              <AdminRoute>
                <AdminLayout>
                  <MediaManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/blogs"
            element={
              <AdminRoute>
                <AdminLayout>
                  <BlogManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/ads"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdsManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/maintenance"
            element={
              <AdminRoute>
                <AdminLayout>
                  <MaintenanceManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/auth-images"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AuthImageManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/support-details"
            element={
              <AdminRoute>
                <AdminLayout>
                  <SupportDetailsPage />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/support-messages"
            element={
              <AdminRoute>
                <AdminLayout>
                  <SupportMessagesPage />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/popular-routes"
            element={
              <AdminRoute>
                <AdminLayout>
                  <PopularRouteManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/popular-cities"
            element={
              <AdminRoute>
                <AdminLayout>
                  <PopularCities />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/footer-routes"
            element={
              <AdminRoute>
                <AdminLayout>
                  <FooterRoutes />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/airports"
            element={
              <AdminRoute>
                <AdminLayout>
                  <Airports />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/rental-packages"
            element={
              <AdminRoute>
                <AdminLayout>
                  <RentalPackages />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/wallet/summary"
            element={
              <AdminRoute>
                <AdminLayout>
                  <WalletSummary />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/wallet/drivers"
            element={
              <AdminRoute>
                <AdminLayout>
                  <WalletDrivers />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/wallet/transactions"
            element={
              <AdminRoute>
                <AdminLayout>
                  <WalletTransactions />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/wallet/withdrawals"
            element={
              <AdminRoute>
                <AdminLayout>
                  <PendingWithdrawals />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/wallet/user-withdrawals"
            element={
              <AdminRoute>
                <AdminLayout>
                  <UserWithdrawals />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/coupons"
            element={
              <AdminRoute>
                <AdminLayout>
                  <CouponManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />


          <Route
            path="/admin/ride-issues"
            element={
              <AdminRoute>
                <AdminLayout>
                  <RideIssues />
                </AdminLayout>
              </AdminRoute>
            }
          />

          {/* Driver Routes */}
          <Route
            path="/driver/dashboard"
            element={
              <DriverRoute>
                <DriverDashboard />
              </DriverRoute>
            }
          />
          <Route
            path="/driver/new-ride-requests"
            element={
              <DriverRoute>
                <NewRideRequests />
              </DriverRoute>
            }
          />
          <Route
            path="/driver/profile"
            element={
              <DriverRoute>
                <DriverProfile />
              </DriverRoute>
            }
          />
          <Route
            path="/driver/current-ride"
            element={
              <DriverRoute>
                <CurrentRide />
              </DriverRoute>
            }
          />
          <Route
            path="/driver/ride-history"
            element={
              <DriverRoute>
                <RideHistory />
              </DriverRoute>
            }
          />
          <Route
            path="/driver/wallet/withdraw"
            element={
              <DriverRoute>
                <WalletWithdraw />
              </DriverRoute>
            }
          />
          <Route
            path="/driver/wallet/history"
            element={
              <DriverRoute>
                <WalletHistory />
              </DriverRoute>
            }
          />
          <Route
            path="/driver/wallet/commission"
            element={
              <DriverRoute>
                <WalletCommission />
              </DriverRoute>
            }
          />

          {/* 404 Not Found */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!pathname.includes("/admin") && !pathname.includes("/corporate") && !pathname.startsWith("/track/") && pathname !== "/track-demo" && (
        <div className={pathname.toLowerCase().startsWith("/cabs") ? "hidden md:block" : ""}>
          <Footer />
        </div>
      )}
    </div>
  );
}

export default App;
